import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Users, Palette, Plus, Trash2, Crown, Upload, Image as ImageIcon, Bell } from "lucide-react";
import { api } from "../lib/api";
import { useEntitlements } from "../context/EntitlementsContext";
import { toast } from "sonner";

/**
 * BusinessAdmin — root-admin tools for Business plan owners (and devs).
 *
 * Iter34 user request: "Business Root Admin & Branding tab — for Business
 * plans, create a root user that manages sub-accounts, plus a branding
 * tab for uploading images, fonts, and colors (Cloud storage feel)."
 *
 * Members tab: list seats + invite by email + remove + promote to admin.
 * Branding tab: company name, logo (data-URL upload), primary + accent
 * colors, font family — these flow back to the FE via /me/business and
 * are applied as CSS vars on the dashboard.
 */
export default function BusinessAdmin() {
  const ent = useEntitlements();
  const [tab, setTab] = useState("members");
  const [annText, setAnnText] = useState("");
  const [annPosting, setAnnPosting] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [annLoading, setAnnLoading] = useState(false);
  // org MUST be declared before any useEffect that references org in its deps
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load org announcements when announcements tab opens
  useEffect(() => {
    if (tab !== "announcements" || !org?.is_root) return;
    setAnnLoading(true);
    api.get("/me/business/announcements").then(({ data }) => {
      setAnnouncements(Array.isArray(data) ? data.reverse() : []);
    }).catch(() => {}).finally(() => setAnnLoading(false));
  }, [tab, org?.is_root]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [saving, setSaving] = useState(false);

  // Branding draft (mirrors org.branding for editing).
  const [brandDraft, setBrandDraft] = useState({
    company_name: "",
    logo_url: "",
    primary_color: "",
    accent_color: "",
    font_family: "",
  });
  const [orgName, setOrgName] = useState("");
  const fileInputRef = useRef(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/business");
      if (data) {
        setOrg(data);
        setOrgName(data.name || "");
        setBrandDraft({
          company_name: data.branding?.company_name || "",
          logo_url: data.branding?.logo_url || "",
          primary_color: data.branding?.primary_color || "",
          accent_color: data.branding?.accent_color || "",
          font_family: data.branding?.font_family || "",
        });
        const m = await api.get("/me/business/members");
        setMembers(m.data || []);
      } else {
        setOrg(null);
      }
    } catch {
      setOrg(null);
    }
    setLoading(false);
  };

  useEffect(() => { if (ent?.loaded) reload(); }, [ent?.loaded]); // eslint-disable-line

  const invite = async (e) => {
    e?.preventDefault?.();
    const em = inviteEmail.trim();
    if (!em) return;
    try {
      await api.post("/me/business/members", { email: em, role: inviteRole });
      setInviteEmail("");
      toast.success(`Invited ${em}`);
      reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "object" ? (detail?.message || "Invite failed") : (detail || "Invite failed");
      toast.error(msg);
    }
  };

  const removeMember = async (m) => {
    if (m.role === "root") return;
    if (!window.confirm(`Remove ${m.invited_email || m.name}?`)) return;
    try {
      await api.delete(`/me/business/members/${m.member_id}`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    }
  };

  const setMemberRole = async (m, role) => {
    try {
      await api.put(`/me/business/members/${m.member_id}`, { role });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const saveBranding = async () => {
    setSaving(true);
    try {
      const body = { name: orgName, branding: brandDraft };
      const { data } = await api.put("/me/business", body);
      setOrg(data);
      // Apply CSS vars immediately so the user sees the change.
      applyBrandingVars(data.branding || {});
      toast.success("Branding saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const onLogoFile = async (file) => {
    if (!file) return;
    if (file.size > 1024 * 1024 * 2) {
      toast.error("Logo must be under 2MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setBrandDraft((b) => ({ ...b, logo_url: e.target.result }));
    };
    reader.readAsDataURL(file);
  };

  if (!ent?.loaded || loading) {
    return <div className="min-h-screen bg-[#0A0A0A] text-[#A1A1AA] flex items-center justify-center text-xs">Loading…</div>;
  }
  // Soft-gate: backend already 404s for non-business; surface a friendly upsell.
  const canUse = ent?.is_dev || ent?.plan === "business";
  if (!canUse) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED] flex flex-col items-center justify-center gap-3 text-center px-6" data-testid="business-paywall">
        <Crown className="w-8 h-8 text-[#E0B355]" />
        <h2 className="font-display text-xl">Business plan required</h2>
        <p className="text-xs text-[#A1A1AA] max-w-sm">Manage seats and customize your team's branding with the Business plan.</p>
        <div className="flex items-center gap-3">
          <Link to="/pricing" className="text-xs px-3 py-1.5 rounded bg-[#5A90F5] text-white font-semibold hover:bg-[#4a80e5]">Upgrade</Link>
          <Link to="/app" className="text-xs text-[#A1A1AA] hover:text-[#EDEDED]">← Back to app</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED]" data-testid="business-admin">
      <header className="border-b border-[#2A2A2A] px-6 py-3 flex items-center justify-between">
        <Link to="/app" className="text-xs text-[#A1A1AA] hover:text-[#EDEDED] inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back to app
        </Link>
        <div className="text-xs text-[#A1A1AA] inline-flex items-center gap-2">
          <span className="bg-[#E0B355]/15 text-[#E0B355] px-2 py-0.5 rounded text-[10px] uppercase tracking-wider">Business</span>
          {org?.name || "Organization"}
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6">
        <div className="flex items-center gap-1 border-b border-[#2A2A2A] mb-6">
          <button
            onClick={() => setTab("members")}
            className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${tab === "members" ? "border-[#5A90F5] text-[#EDEDED]" : "border-transparent text-[#A1A1AA] hover:text-[#EDEDED]"}`}
            data-testid="business-tab-members"
          >
            <Users className="w-3 h-3 inline-block mr-1.5" /> Members
          </button>
          <button
            onClick={() => setTab("branding")}
            className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${tab === "branding" ? "border-[#5A90F5] text-[#EDEDED]" : "border-transparent text-[#A1A1AA] hover:text-[#EDEDED]"}`}
            data-testid="business-tab-branding"
          >
            <Palette className="w-3 h-3 inline-block mr-1.5" /> Branding
          </button>
          {org?.is_root && (
            <button
              onClick={() => setTab("announcements")}
              className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${tab === "announcements" ? "border-[#5A90F5] text-[#EDEDED]" : "border-transparent text-[#A1A1AA] hover:text-[#EDEDED]"}`}
              data-testid="business-tab-announcements"
            >
              <Bell className="w-3 h-3 inline-block mr-1.5" /> Announcements
            </button>
          )}
        </div>

        {tab === "members" && (
          <section className="space-y-5" data-testid="business-members-section">
            {org?.is_root && (
              <form onSubmit={invite} className="flex items-center gap-2 bg-[#141414] border border-[#2A2A2A] rounded-lg p-3">
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="teammate@company.com"
                  className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-xs text-[#EDEDED] outline-none focus:border-[#5A90F5]"
                  data-testid="business-invite-email"
                />
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-xs text-[#EDEDED] outline-none focus:border-[#5A90F5]"
                  data-testid="business-invite-role"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
                <button
                  type="submit"
                  className="text-xs px-3 py-1.5 rounded bg-[#5A90F5] text-white font-semibold hover:bg-[#4a80e5] inline-flex items-center gap-1"
                  data-testid="business-invite-submit"
                >
                  <Plus className="w-3 h-3" /> Invite
                </button>
              </form>
            )}

            <div className="bg-[#141414] border border-[#2A2A2A] rounded-lg overflow-hidden">
              <div className="px-4 py-2 border-b border-[#2A2A2A] text-[10px] uppercase tracking-wider text-[#A1A1AA]">
                Seats: {members.length} / {org?.seats || 1}
              </div>
              {members.length === 0 && <div className="px-4 py-6 text-xs text-[#A1A1AA] text-center">No members yet.</div>}
              {members.map((m) => (
                <div
                  key={m.member_id}
                  className="flex items-center gap-3 px-4 py-2.5 border-b border-[#2A2A2A] last:border-b-0 hover:bg-[#1a1a1a]"
                  data-testid={`business-member-${m.member_id}`}
                >
                  <div className="w-7 h-7 rounded-full bg-[#0A0A0A] border border-[#2A2A2A] overflow-hidden flex items-center justify-center flex-shrink-0">
                    {m.picture ? <img src={m.picture} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px]">{((m.name || m.invited_email) || "?")[0]?.toUpperCase()}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[#EDEDED] truncate flex items-center gap-1.5">
                      {m.name || m.invited_email}
                      {m.role === "root" && <Crown className="w-3 h-3 text-[#E0B355]" />}
                    </div>
                    <div className="text-[10px] text-[#A1A1AA] truncate">{m.invited_email}</div>
                  </div>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider ${m.status === "active" ? "bg-[#22C55E]/15 text-[#22C55E]" : "bg-[#E0B355]/15 text-[#E0B355]"}`}>
                    {m.status}
                  </span>
                  {org?.is_root && m.role !== "root" && (
                    <>
                      <select
                        value={m.role}
                        onChange={(e) => setMemberRole(m, e.target.value)}
                        className="text-[10px] bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1 py-0.5 text-[#EDEDED]"
                        data-testid={`business-member-role-${m.member_id}`}
                      >
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                      </select>
                      <button
                        onClick={() => removeMember(m)}
                        className="p-1 rounded hover:bg-[#FF3B30]/10 text-[#FF3B30]"
                        title="Remove"
                        data-testid={`business-member-remove-${m.member_id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {tab === "branding" && (
          <section className="space-y-5" data-testid="business-branding-section">
            {!org?.is_root ? (
              <div className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-4 text-xs text-[#A1A1AA]">Only the root admin can edit branding.</div>
            ) : (
              <>
                <div className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-5 space-y-4">
                  <div>
                    <label className="meta-label block mb-1">Organization name</label>
                    <input
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-xs text-[#EDEDED] outline-none focus:border-[#5A90F5]"
                      data-testid="business-org-name"
                    />
                  </div>
                  <div>
                    <label className="meta-label block mb-1">Company name (shown in branding)</label>
                    <input
                      value={brandDraft.company_name}
                      onChange={(e) => setBrandDraft({ ...brandDraft, company_name: e.target.value })}
                      className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-xs text-[#EDEDED] outline-none focus:border-[#5A90F5]"
                      data-testid="business-brand-company"
                    />
                  </div>

                  <div>
                    <label className="meta-label block mb-1">Logo</label>
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-16 rounded bg-[#0A0A0A] border border-[#2A2A2A] flex items-center justify-center overflow-hidden flex-shrink-0">
                        {brandDraft.logo_url ? <img src={brandDraft.logo_url} alt="logo" className="w-full h-full object-contain" /> : <ImageIcon className="w-5 h-5 text-[#52525B]" />}
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => onLogoFile(e.target.files?.[0])}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="text-xs px-2.5 py-1.5 rounded border border-[#2A2A2A] text-[#EDEDED] hover:bg-[#1a1a1a] inline-flex items-center gap-1"
                        data-testid="business-brand-upload"
                      >
                        <Upload className="w-3 h-3" /> Upload
                      </button>
                      {brandDraft.logo_url && (
                        <button
                          type="button"
                          onClick={() => setBrandDraft({ ...brandDraft, logo_url: "" })}
                          className="text-xs text-[#A1A1AA] hover:text-[#FF3B30]"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="text-[10px] text-[#52525B] mt-1">PNG / JPG, under 2MB. Stored as a data URL.</div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="meta-label block mb-1">Primary color</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={brandDraft.primary_color || "#5A90F5"}
                          onChange={(e) => setBrandDraft({ ...brandDraft, primary_color: e.target.value })}
                          className="w-10 h-8 bg-transparent border border-[#2A2A2A] rounded cursor-pointer"
                          data-testid="business-brand-primary"
                        />
                        <input
                          value={brandDraft.primary_color || ""}
                          placeholder="#5A90F5"
                          onChange={(e) => setBrandDraft({ ...brandDraft, primary_color: e.target.value })}
                          className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-xs font-mono text-[#EDEDED] outline-none focus:border-[#5A90F5]"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="meta-label block mb-1">Accent color</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={brandDraft.accent_color || "#E0B355"}
                          onChange={(e) => setBrandDraft({ ...brandDraft, accent_color: e.target.value })}
                          className="w-10 h-8 bg-transparent border border-[#2A2A2A] rounded cursor-pointer"
                          data-testid="business-brand-accent"
                        />
                        <input
                          value={brandDraft.accent_color || ""}
                          placeholder="#E0B355"
                          onChange={(e) => setBrandDraft({ ...brandDraft, accent_color: e.target.value })}
                          className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-xs font-mono text-[#EDEDED] outline-none focus:border-[#5A90F5]"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="meta-label block mb-1">Font family</label>
                    <select
                      value={brandDraft.font_family || ""}
                      onChange={(e) => setBrandDraft({ ...brandDraft, font_family: e.target.value })}
                      className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-xs text-[#EDEDED] outline-none focus:border-[#5A90F5] cursor-pointer"
                      data-testid="business-brand-font"
                      style={{ fontFamily: brandDraft.font_family || "inherit" }}
                    >
                      <option value="">Default (system)</option>
                          <option value="Inter, sans-serif" style={{ fontFamily: "Inter, sans-serif" }}>Inter</option>
                          <option value="DM Sans, sans-serif" style={{ fontFamily: "DM Sans, sans-serif" }}>DM Sans</option>
                          <option value="IBM Plex Mono, monospace" style={{ fontFamily: "IBM Plex Mono, monospace" }}>IBM Plex Mono</option>
                          <option value="Georgia, serif" style={{ fontFamily: "Georgia, serif" }}>Georgia</option>
                          <option value="Arial, sans-serif" style={{ fontFamily: "Arial, sans-serif" }}>Arial</option>
                          <option value="Helvetica Neue, sans-serif" style={{ fontFamily: "Helvetica Neue, sans-serif" }}>Helvetica Neue</option>
                          <option value="Trebuchet MS, sans-serif" style={{ fontFamily: "Trebuchet MS, sans-serif" }}>Trebuchet MS</option>
                          <option value="Verdana, sans-serif" style={{ fontFamily: "Verdana, sans-serif" }}>Verdana</option>
                          <option value="Times New Roman, serif" style={{ fontFamily: "Times New Roman, serif" }}>Times New Roman</option>
                          <option value="Courier New, monospace" style={{ fontFamily: "Courier New, monospace" }}>Courier New</option>
                          <option value="Palatino, serif" style={{ fontFamily: "Palatino, serif" }}>Palatino</option>
                    </select>
                    <div className="text-[10px] text-[#52525B] mt-1">Font previews shown in the dropdown.</div>
                  </div>

                  {/* Brand highlight roster — inline colour picker */}
                  <div>
                    <label className="meta-label block mb-2">Brand Colour Roster</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(brandDraft.highlight_roster || []).map((col, i) => (
                        <div key={i} className="relative group">
                          <label className="w-8 h-8 rounded-lg cursor-pointer border border-[#2A2A2A] block overflow-hidden"
                            style={{ background: col }}>
                            <input type="color" value={col}
                              onChange={e => {
                                const r = [...(brandDraft.highlight_roster || [])];
                                r[i] = e.target.value;
                                setBrandDraft({ ...brandDraft, highlight_roster: r });
                              }}
                              className="opacity-0 w-full h-full cursor-pointer absolute inset-0" />
                          </label>
                          <button onClick={() => {
                              const r = [...(brandDraft.highlight_roster || [])];
                              r.splice(i, 1);
                              setBrandDraft({ ...brandDraft, highlight_roster: r });
                            }}
                            className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[#FF3B30] text-white text-[8px] hidden group-hover:flex items-center justify-center">✕</button>
                        </div>
                      ))}
                      <button onClick={() => setBrandDraft({ ...brandDraft, highlight_roster: [...(brandDraft.highlight_roster || []), "#5A90F5"] })}
                        className="w-8 h-8 rounded-lg border border-dashed border-[#3A3A3A] text-[#52525B] hover:border-[#5A90F5] hover:text-[#5A90F5] flex items-center justify-center text-lg">+</button>
                    </div>
                    <div className="text-[10px] text-[#52525B]">Brand colours shared with all org members in their highlight palette.</div>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-2">
                    <button
                      onClick={saveBranding}
                      disabled={saving}
                      className="text-xs px-4 py-1.5 rounded bg-[#5A90F5] text-white font-semibold hover:bg-[#4a80e5] disabled:opacity-60"
                      data-testid="business-brand-save"
                    >
                      {saving ? "Saving…" : "Save Branding"}
                    </button>
                  </div>
                </div>

                {/* Live preview */}
                <div className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-5">
                  <div className="text-[10px] uppercase tracking-wider text-[#A1A1AA] mb-3">Preview</div>
                  <div
                    className="rounded p-4 flex items-center gap-3"
                    style={{
                      background: brandDraft.primary_color || "#141414",
                      color: "#fff",
                      fontFamily: brandDraft.font_family || undefined,
                    }}
                  >
                    {brandDraft.logo_url && <img src={brandDraft.logo_url} alt="" className="w-8 h-8 rounded bg-white object-contain p-0.5" />}
                    <div>
                      <div className="text-sm font-bold">{brandDraft.company_name || orgName || "Your Company"}</div>
                      <div className="text-xs opacity-80">Powered by Crescendo</div>
                    </div>
                    <button
                      type="button"
                      className="ml-auto text-xs px-2.5 py-1 rounded font-semibold"
                      style={{ background: brandDraft.accent_color || "#E0B355", color: "#000" }}
                    >
                      Action
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>
        )}
      </main>

      {/* Announcements tab content */}
      {tab === "announcements" && org?.is_root && (
        <main className="max-w-4xl mx-auto p-6 space-y-6">
          <section>
            <h3 className="text-sm font-semibold text-[#EDEDED] mb-1">New Announcement</h3>
            <p className="text-[11px] text-[#A1A1AA] mb-3">Pushes to all active org members' inboxes and shows at the top of the app.</p>
            <textarea value={annText} onChange={e => setAnnText(e.target.value)}
              placeholder="Write your announcement…" rows={3}
              className="w-full bg-[#141414] border border-[#2A2A2A] rounded-lg px-3 py-2 text-sm text-[#EDEDED] outline-none focus:border-[#5A90F5] resize-none" />
            <button
              onClick={async () => {
                if (!annText.trim()) return;
                setAnnPosting(true);
                try {
                  const { data } = await api.post("/me/business/announcement", { text: annText.trim() });
                  toast.success("Announcement pushed to all members");
                  setAnnouncements(prev => [{ id: data.ann_id, text: annText.trim(), created_at: new Date().toISOString() }, ...prev]);
                  setAnnText("");
                } catch { toast.error("Failed to push announcement"); }
                setAnnPosting(false);
              }}
              disabled={!annText.trim() || annPosting}
              className="mt-2 px-4 py-1.5 rounded bg-[#5A90F5] text-white text-xs font-semibold disabled:opacity-50">
              {annPosting ? "Posting…" : "Push to Organization"}
            </button>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-[#EDEDED] mb-3">Active Announcements</h3>
            {annLoading ? (
              <p className="text-[11px] text-[#52525B]">Loading…</p>
            ) : announcements.length === 0 ? (
              <p className="text-[11px] text-[#3F3F46]">No active announcements.</p>
            ) : (
              <div className="space-y-2">
                {announcements.map(ann => (
                  <div key={ann.id} className="flex items-start gap-3 p-3 rounded-lg border border-[#2A2A2A] bg-[#141414]">
                    <p className="flex-1 text-[12px] text-[#EDEDED]">{ann.text}</p>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={async () => {
                          if (!window.confirm("Force-revoke from all members' inboxes?")) return;
                          try {
                            await api.post(`/me/business/announcement/${ann.id}/revoke`);
                            setAnnouncements(prev => prev.filter(a => a.id !== ann.id));
                            toast.success("Announcement revoked from all inboxes");
                          } catch { toast.error("Revoke failed"); }
                        }}
                        className="text-[9px] px-1.5 py-0.5 rounded border border-[#E0B355]/40 text-[#E0B355] hover:bg-[#E0B355]/10 transition-colors"
                        title="Remove from all members' inboxes">
                        Force Revoke
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await api.delete(`/me/business/announcement/${ann.id}`);
                            setAnnouncements(prev => prev.filter(a => a.id !== ann.id));
                            toast.success("Announcement removed");
                          } catch { toast.error("Failed to remove"); }
                        }}
                        className="flex-shrink-0 p-1 rounded hover:bg-[#FF3B30]/10 text-[#52525B] hover:text-[#FF3B30] transition-colors"
                        title="Remove announcement">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </main>
      )}
    </div>
  );
}

/**
 * Apply branding CSS vars on the dashboard. Exposed so App.js can call
 * it once /me/business resolves.
 */
export function applyBrandingVars(branding) {
  if (!branding) return;
  const root = document.documentElement;
  if (branding.primary_color) root.style.setProperty("--cresc-brand-primary", branding.primary_color);
  if (branding.accent_color) root.style.setProperty("--cresc-brand-accent", branding.accent_color);
  if (branding.font_family) root.style.setProperty("--cresc-brand-font", branding.font_family);
}
